import keycloak from '../auth/keycloak'
import { pickPrimaryRole } from '../auth/rolePriority'
import { prepareUploadImage } from './prepareUploadImage'
import type { UploadDocumentType } from './photoDocuments'
import { dropThisBrowserPush } from './pushLocal'
import { MAX_ORS_SEGMENT_KM, ORS_AVOID_COUNTRIES, TRANS_SIBERIAN_CORRIDOR } from './routingConfig'
import type { RoutePlan, RoutePlanRequest } from './routePlan'
import { vehicleTypeLabel } from './vehicleTypes'

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export class ApiError extends Error {
  readonly code: string
  readonly status: number
  /**
   * Raw, untranslated response body kept for diagnostics when the server did not answer with
   * our JSON envelope (an nginx 502 page, a proxy timeout). Log it, never render it: it is
   * arbitrary text from an intermediary and means nothing to a user.
   */
  readonly detail?: string

  constructor(code: string, message: string, status: number, detail?: string) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.detail = detail
  }
}

/**
 * Fallback text for a failed request when the backend did not send its own `message`.
 *
 * The backend normally answers `{code, message}` with a translated Russian string (see
 * internal/validation FriendlyMessage), and that always wins. This covers the other case —
 * a 502 from nginx, a gateway timeout, an empty body — where we used to surface the literal
 * "HTTP 502". That told the user nothing actionable and told support nothing either.
 */
function messageForStatus(status: number): string {
  if (status === 401 || status === 403) return 'Недостаточно прав. Войдите заново и повторите.'
  if (status === 404) return 'Не найдено — возможно, запись уже удалили.'
  if (status === 409) return 'Данные изменились. Обновите страницу и повторите действие.'
  if (status === 413) return 'Файл слишком большой.'
  if (status === 429) return 'Слишком много действий подряд. Подождите немного и попробуйте снова.'
  if (status >= 500) return 'Сбой на сервере. Мы уже знаем о проблеме — попробуйте через минуту.'
  if (status >= 400) return 'Что-то пошло не так. Попробуйте ещё раз.'
  return 'Что-то пошло не так. Попробуйте ещё раз.'
}

/**
 * Safe user-facing text for anything thrown by an api.* call.
 *
 * Use this instead of rendering `e.message` directly: a caught value is not necessarily an
 * ApiError (a TypeError from our own code, a rejected non-Error) and its message can be
 * English or a stack-shaped string. ApiError messages are already human and Russian by the
 * time they get here — from the backend envelope or from messageForStatus above.
 */
export function friendlyError(e: unknown, fallback = 'Не удалось выполнить операцию. Попробуйте ещё раз.'): string {
  return e instanceof ApiError ? e.message : fallback
}

export type PlanStatus = {
  plan_code: string
  effective_limit: number
  free_base: number
  referral_bonus_slots: number
  tracking_credits: number
  plan_expires_at?: string | null
  /** The paid plan that ran out in the last few days (the account is on free now). */
  ended_plan_code?: string
  ended_at?: string | null
}

export type PlanCatalogItem = {
  code: string
  name: string
  concurrent_limit: number
  doc_retention_days: number
  price_kopecks: number
}

export type CheckoutResult = {
  payment_id?: string
  confirmation_url?: string
  activated?: boolean
  applied_coupon?: string
}

// What buying one paid plan would do right now (GET /billing/plan/offers). The resulting term is
// computed by the backend with the same function that later credits the payment.
export type PlanOffer = {
  plan_code: string
  action: 'buy' | 'extend' | 'upgrade' | 'unavailable'
  price_kopecks: number
  applied_coupon?: string
  result_plan_code?: string
  result_expires_at?: string
  /** action=unavailable: when the higher plan that blocks it ends. */
  available_after?: string
}

export type PaymentSyncStatus = 'succeeded' | 'pending' | 'canceled'
// What a payment bought: a tariff period or «отслеживания» for the wallet.
export type PaymentKind = 'plan' | 'oneoff'

// One line of «История платежей» (GET /billing/payments), newest first.
export type PaymentHistoryItem = {
  id: string
  kind: 'plan' | 'oneoff'
  plan_code?: string
  /** plan: the period it bought. */
  days?: number
  /** oneoff: the «отслеживания» it bought. */
  units?: number
  amount_kopecks: number
  status: PaymentSyncStatus
  coupon_code?: string
  created_at: string
  paid_at?: string
}

// One line of the «отслеживания» statement (GET /billing/credits), newest first.
export type CreditEvent = {
  id: number
  /** +7 bought / granted, −2 spent. */
  delta: number
  balance_after: number
  reason: 'purchase' | 'referral' | 'trip' | 'renewal' | 'adjustment'
  payment_id?: string
  shipment_id?: string
  /** referral: how many invitees the reached tier needed. */
  referral_tier?: number
  created_at: string
  /** purchase: what it cost. */
  amount_kopecks?: number
  /** «Заявка 1542»; absent when the line has no trip or the trip was deleted. */
  shipment_title?: string
  /** «Москва → Казань» */
  shipment_route?: string
}

// What the tariff is used for right now and this month (GET /billing/usage).
export type BillingUsage = {
  /** Trips in transit that occupy the tariff's machines. */
  slots_in_use: number
  /** Trips in transit paid from the wallet — they occupy no machine. */
  wallet_in_progress: number
  trips_this_month: number
  month_start: string
}

// Wire shape for notification settings (snake_case to match the backend).
// The UI uses a camelCase NotificationSettings type and maps to/from this.
export type NotificationSettingsDTO = {
  push_enabled: boolean
  /** Sent as true only by a screen that shows the category switches (lib/pushCategories); without
   *  it the server takes push_enabled alone and keeps the categories as they were. */
  categories?: boolean
  events: {
    new_shipment: boolean
    status_changed: boolean
    arrival_at_point: boolean
    route_issues: boolean
    documents: boolean
    billing?: boolean
    long_stops?: boolean
  }
}

export type ReferralCoupon = {
  code: string
  plan_code: string
  price_kopecks: number
  label: string
}

export type ReferralTier = {
  threshold: number
  /** What the threshold counts: invitees with a completed, really-tracked trip, or paying ones. */
  basis?: 'trip' | 'paid'
  title: string
  description: string
  reached: boolean
}

export type ReferralStatus = {
  code: string
  link: string
  qualified_count: number
  /** Invitees who paid for a tariff with money — what the coupon/credit tiers count. */
  paid_count?: number
  bonus_active: boolean
  bonus_slots: number
  tracking_credits: number
  coupons: ReferralCoupon[]
  tiers: ReferralTier[]
  /** This manager came by a colleague's invitation (a code is already linked). */
  referred?: boolean
  /**
   * A colleague's code may still be entered: only a new manager — not linked yet, no paid tariff,
   * no finished trip. The «enter a code» field is shown only then.
   */
  can_claim?: boolean
}

/** One day of the driver invite funnel: every count is a number of invites (shipments). */
export type DriverFunnelDay = {
  day: string
  invites: number
  welcome_shown: number
  welcome_continue: number
  opened_signed_in: number
  opened_app: number
  welcome_then_app: number
  accepted: number
  tracking_started: number
  tracking_from_app: number
}

export type DriverFunnelReport = {
  since: string
  days: number
  totals: DriverFunnelDay
  by_day: DriverFunnelDay[]
}

/** Managers registered in the period and how many of them reached each step. */
export type GrowthFunnel = {
  registered: number
  created_shipment: number
  invited_driver: number
  driver_accepted: number
  activated: number
  viewed_tracking: number
  second_shipment: number
  second_tracked: number
  repeat_7d: number
  repeat_14d: number
  repeat_30d: number
  hit_limit: number
  viewed_pricing: number
  started_checkout: number
  paid: number
}

export type GrowthRevenue = {
  mrr_rub: number
  paying_now: number
  new_paying: number
  new_mrr_rub: number
  renewals: number
  churned: number
  churned_mrr_rub: number
  arpu_rub: number
  arppu_rub: number
}

export type GrowthRetention = {
  points: { day: number; eligible: number; retained: number }[]
  w4_eligible: number
  w4_retained: number
}

export type GrowthSource = { source: string; registered: number; activated: number; paid: number }

export type GrowthWeek = {
  week: string
  new_managers: number
  new_drivers: number
  shipments_created: number
  shipments_tracked: number
  active_managers: number
  limit_hit_managers: number
  paying_managers: number
  revenue_rub: number
}

export type GrowthReport = {
  since: string
  days: number
  funnel: GrowthFunnel
  ttfv: { median_hours: number | null; p75_hours: number | null; p90_hours: number | null }
  drivers: { registered: number; tracked: number; from_app: number }
  money: { checkouts: number; payments: number; paying_managers: number; revenue_rub: number }
  revenue: GrowthRevenue
  retention: GrowthRetention
  sources: GrowthSource[]
  public_links: { created: number; opened: number }
  landing: {
    views: number
    cta_clicks: number
    store_clicks: number
    by_source: { source: string; views: number; cta_clicks: number; store_clicks: number; registered: number }[]
  }
  /** null when Keycloak could not be asked. */
  signups: { created: number; email_verified: number; signed_in: number } | null
  self_reported: { answer: string; count: number }[]
  permissions: { granted: number; denied: number; background_granted: number; background_denied: number }
  weeks: GrowthWeek[]
}

const BASE_URL = import.meta.env.VITE_API_BASE || ''

/** Backend code for "valid token, but this account owes us the 152-ФЗ consent". */
export const CONSENT_REQUIRED_CODE = 'CONSENT_REQUIRED'

/** 401 code meaning the account behind this token was deleted by its owner. */
export const ACCOUNT_DELETED_CODE = 'ACCOUNT_DELETED'
/** Window event raised when any request comes back with that code. */
export const CONSENT_REQUIRED_EVENT = 'lupa:consent-required'

/** Returns the current Keycloak access token (used by WS hooks for ?token= query param). */
export function getToken(): string | null {
  return keycloak.token ?? null
}

/** Returns the primary app role (manager/driver) from the Keycloak token. */
export function getRole(): string | null {
  const tp = keycloak.tokenParsed as Record<string, unknown> | undefined
  const roles = (tp?.realm_access as { roles?: string[] } | undefined)?.roles ?? []
  // Shares the priority with AuthProvider on purpose: a second copy of this rule would drift,
  // and the drift would only show up as "the same account opens a different cabinet here".
  return pickPrimaryRole(roles) || null
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  // Proactively refresh if token expires within 30 seconds
  try {
    await keycloak.updateToken(30)
  } catch {
    // keycloak-js clears its tokens only when the token endpoint answered 400
    // (refresh token expired/revoked) — that is a genuine session death and the
    // only case worth a login redirect. Every other failure (network blip, KC
    // briefly unreachable, a 502 during deploy) must NOT log the user out: we
    // serve the request with the still-valid access token, or fail just this
    // request as retriable when the access token has already expired too.
    if (!keycloak.refreshToken) {
      keycloak.login()
      throw new ApiError('SESSION_EXPIRED', 'Нужно войти снова, чтобы продолжить работу', 401)
    }
    if (keycloak.isTokenExpired()) {
      throw new ApiError('NETWORK_ERROR', 'Нет соединения с сервером. Проверьте интернет и повторите.', 0)
    }
  }

  const token = keycloak.token
  const isFormData = options.body instanceof FormData

  let res: Response
  try {
    res = await fetch(BASE_URL + path, {
      ...options,
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
      credentials: 'include',
    })
  } catch {
    // fetch itself rejected — the request never reached the backend. Surface it
    // as a retriable ApiError(status 0) so callers can tell "offline" from an
    // HTTP error without parsing TypeError messages.
    throw new ApiError('NETWORK_ERROR', 'Нет соединения с сервером. Проверьте интернет и повторите.', 0)
  }

  if (!res.ok) {
    let code = `HTTP_${res.status}`
    let message = ''
    let detail: string | undefined
    try {
      const body = await res.json()
      if (body?.code) code = body.code
      if (body?.message) message = body.message
    } catch {
      // Not our JSON envelope — keep the raw text for the console only, capped so a full
      // HTML error page cannot flood the log.
      detail = (await res.text().catch(() => '')).slice(0, 500) || undefined
    }
    if (!message) message = messageForStatus(res.status)
    // A 401 here means we hold a token the backend rejects (e.g. it was signed
    // by a Keycloak instance that has since been recreated). Drop it and force
    // one fresh login. The guard prevents a redirect loop: if even a freshly
    // issued token is rejected (a persistent config issue, not a stale token),
    // we surface the error instead of bouncing through Keycloak every second.
    // The account was deleted by its owner, possibly in another tab. Re-logging in cannot help —
    // there is nothing to log into — so sign out instead of bouncing through Keycloak.
    if (res.status === 401 && code === ACCOUNT_DELETED_CODE) {
      keycloak.clearToken()
      // The server already dropped the account's push rows; only this browser still holds its end.
      await dropThisBrowserPush()
      keycloak.logout({ redirectUri: window.location.origin })
    } else if (res.status === 401 && !sessionStorage.getItem('kc_relogin_attempted')) {
      sessionStorage.setItem('kc_relogin_attempted', '1')
      keycloak.clearToken()
      keycloak.login()
    }
    // The account exists in Keycloak but has never given the 152-ФЗ consent (social login).
    // Any endpoint can be the first to hit this, so announce it globally: the consent gate
    // listens and takes over the screen instead of every caller rendering its own error.
    if (res.status === 403 && code === CONSENT_REQUIRED_CODE) {
      window.dispatchEvent(new CustomEvent(CONSENT_REQUIRED_EVENT))
    }
    throw new ApiError(code, message, res.status, detail)
  }
  // Successful request — clear the one-shot guard so a later genuine token
  // expiry can trigger a fresh re-login again.
  sessionStorage.removeItem('kc_relogin_attempted')
  const ct = res.headers.get('content-type') || ''
  if (ct.includes('application/json')) {
    return res.json() as Promise<T>
  }
  // @ts-expect-error allow non-json responses
  return undefined
}

// Public, token-only request: NO Keycloak. The public tracking endpoint authenticates via the
// share JWT in the query string, so an unauthenticated customer must reach it without a KC session.
// Routing it through request() above would call keycloak.updateToken()/login() and bounce a
// logged-out visitor to the Keycloak login page — defeating the "view without registration" design.
// Errors are surfaced as ApiError (status preserved) so callers can distinguish 403 = link revoked.
// A public page is opened on flaky mobile networks, where a request can hang instead of failing.
// Without a limit the page would wait forever and never retry.
export const PUBLIC_REQUEST_TIMEOUT_MS = 15_000

async function publicRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), PUBLIC_REQUEST_TIMEOUT_MS)
  const abortWithCaller = () => controller.abort()
  options.signal?.addEventListener('abort', abortWithCaller)
  const networkError = () =>
    new ApiError('NETWORK_ERROR', 'Нет соединения с сервером. Проверьте интернет и повторите.', 0)

  try {
    let res: Response
    try {
      res = await fetch(BASE_URL + path, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...(options.headers || {}),
        },
      })
    } catch {
      throw networkError()
    }

    if (!res.ok) {
      let code = `HTTP_${res.status}`
      let message = ''
      let detail: string | undefined
      try {
        const body = await res.json()
        if (body?.code) code = body.code
        if (body?.message) message = body.message
      } catch {
        detail = (await res.text().catch(() => '')).slice(0, 500) || undefined
      }
      if (!message) message = messageForStatus(res.status)
      throw new ApiError(code, message, res.status, detail)
    }

    const ct = res.headers.get('content-type') || ''
    if (ct.includes('application/json')) {
      // The body can stall too; the same timeout covers it.
      return await (res.json() as Promise<T>).catch(() => { throw networkError() })
    }
    // @ts-expect-error allow non-json responses
    return undefined
  } finally {
    clearTimeout(timer)
    options.signal?.removeEventListener('abort', abortWithCaller)
  }
}

export async function fetchWSTicket(): Promise<string> {
  const { ticket } = await request<{ ticket: string }>('/api/ws-ticket', { method: 'POST' })
  return ticket
}

export async function fetchDocTicket(): Promise<string> {
  const { ticket } = await request<{ ticket: string }>('/api/documents/ticket', { method: 'POST' })
  return ticket
}

/** Driver-scoped stream ticket — lets a driver open documents on shipments they're assigned to. */
export async function fetchDriverDocTicket(): Promise<string> {
  const { ticket } = await request<{ ticket: string }>('/api/driver/documents/ticket', { method: 'POST' })
  return ticket
}

export type DocumentAccessScope = 'driver' | 'manager'

/**
 * Downloads one document image into memory. The ticket is single-use: it is spent by this one
 * request, so a retry must mint a new one — the caller just calls this again. The stream URL never
 * reaches the address bar, the history or a new tab, only this fetch.
 */
export async function fetchDocumentImage(documentId: string, scope: DocumentAccessScope, signal?: AbortSignal): Promise<Blob> {
  return fetchTicketedFile((ticket) => `/api/public/documents/${encodeURIComponent(documentId)}/stream?ticket=${encodeURIComponent(ticket)}`, scope, signal)
}

/**
 * Downloads one of the manager's files for the driver (a photo or a PDF) into memory, through the
 * same single-use ticket as documents.
 */
export async function fetchAttachmentFile(attachmentId: string, scope: DocumentAccessScope, signal?: AbortSignal): Promise<Blob> {
  return fetchTicketedFile((ticket) => `/api/public/attachments/${encodeURIComponent(attachmentId)}/file?ticket=${encodeURIComponent(ticket)}`, scope, signal)
}

async function fetchTicketedFile(path: (ticket: string) => string, scope: DocumentAccessScope, signal?: AbortSignal): Promise<Blob> {
  const ticket = scope === 'driver' ? await fetchDriverDocTicket() : await fetchDocTicket()
  let res: Response
  try {
    res = await fetch(`${BASE_URL}${path(ticket)}`, { signal, cache: 'no-store' })
  } catch (error) {
    if (signal?.aborted) throw error
    throw new ApiError('NETWORK_ERROR', 'Нет соединения с сервером. Проверьте интернет и повторите.', 0)
  }
  if (!res.ok) {
    let code = `HTTP_${res.status}`
    try {
      const body = await res.json()
      if (body?.code) code = body.code
    } catch {
      // not our envelope — the status is enough
    }
    throw new ApiError(code, messageForStatus(res.status), res.status)
  }
  try {
    return await res.blob()
  } catch (error) {
    if (signal?.aborted) throw error
    throw new ApiError('NETWORK_ERROR', 'Связь оборвалась, документ не загрузился. Повторите.', 0)
  }
}

export type Shipment = {
  id: string
  status: string
  owner_id?: string
  planned_delivery_time?: string
  route_json?: unknown
  // The manager's note to the driver. Only the owner and the driver who accepted the trip get it.
  driver_comment?: string
  // How this shipment's tracking is paid for: "subscription" (a plan slot, no timer) or
  // "wallet" (N «отслеживания» spent at «Начать рейс», leased for N×3 суток from the first fix).
  tracking_funding?: string
  tracking_units?: number
  // Lease end for wallet-funded tracking (from the first GPS fix); absent = no timer or no fix yet.
  tracking_expires_at?: string | null
  // When the driver pressed «Начать рейс»; absent — an accepted trip has not started.
  trip_started_at?: string | null
  // The trip's first GPS fix: the paid period runs from it.
  tracking_started_at?: string | null
  created_at: string
  // When the shipment first reached a terminal status (completed/delivered);
  // absent while still in progress. Stamped once on the backend.
  completed_at?: string | null
  // The driver pressed «Понятно» on this cancelled shipment: it is out of «Отменены менеджером»
  // (the driver's own list only).
  cancel_seen?: boolean
  // The driver asked to finish the trip; it stays in_progress until the manager confirms
  // (completeShipment) or rejects, or completes on its own at completion_due_at.
  completion_requested_at?: string | null
  completion_due_at?: string | null
  // When the manager last turned such a request down.
  completion_rejected_at?: string | null
  // Control reasons the manager closed by hand («больше не требует контроля»), keyed by reason code.
  // A mark holds only while the shipment keeps the status it was set for.
  control_acknowledgements?: Record<string, { status_code: string; acknowledged_at?: string }> | null
  // Latest GPS fix, resolved server-side and embedded in list/detail summaries so the UI can
  // render "last updated" without a separate per-shipment tracking request. Absent = no fix yet.
  last_location?: { latitude: number; longitude: number; timestamp: string } | null
  // Enriched summary fields returned by manager/client list + detail endpoints.
  effective_status?: string
  assignment?: AssignmentSummary | null
  document_count?: number
  // Activity, transit and control tasks as the server decided them — never recomputed here.
  operational?: ManagerShipmentState | null
}

// One transport option from the backend catalog (single source of truth for codes + labels).
export type VehicleTypeOption = {
  code: string
  label: string
  short_label?: string
  // The class's typical figures — where route planning's sliders start; absent for «Спецтранспорт».
  payload_kg?: number
  body_m3_min?: number
  body_m3_max?: number
}

// Optional server-side filters for the manager shipment archive. Dates are ISO strings and
// apply to the completion date; vehicle_type is the catalog CODE (not the label).
export type ShipmentHistoryParams = {
  status?: string
  vehicleType?: string
  q?: string
  from?: string
  to?: string
  limit?: number
  offset?: number
}

// One page of the archive plus the total count of matching rows (for pagination).
export type ShipmentHistoryResult = { items: Shipment[]; total: number }

// GeoPoint is one waypoint sent to the server for the authoritative price quote.
export type GeoPoint = { lat: number; lng: number }

// TrackingQuote is the server-computed estimate for funding a shipment from the wallet.
export type TrackingQuote = {
  distance_km: number
  days: number
  units: number
  price_kopecks: number
  unit_days: number
}

export type CreateShipmentPayload = {
  planned_delivery_time?: string
  route_json?: unknown
  // Left out, an edit keeps the stored note; an empty string clears it.
  driver_comment?: string
}

// One of the manager's files for the driver on a shipment: a photo (stored as JPEG) or a PDF.
export type ShipmentAttachment = {
  id: string
  shipment_id: string
  file_name: string
  content_type: 'image/jpeg' | 'application/pdf' | string
  size_bytes: number
  uploaded_at: string
}

// The same limits the server enforces, shown before anything is sent.
export const ATTACHMENT_LIMITS = {
  maxFiles: 10,
  maxBytes: 10_000_000,
  accept: 'image/jpeg,image/png,image/gif,image/heic,image/heif,application/pdf,.pdf',
} as const

// Live tracking snapshot for a shipment: the actually-driven GPS breadcrumb
// (positions) + last known point. Returned by the manager-scoped snapshot endpoints.
// There is no server-side planned route — the planned polyline is computed
// client-side from route_json.
export type TrackSnapshot = {
  shipment_id: string
  status: string
  positions: [number, number][]
  last?: { latitude?: number; longitude?: number; timestamp?: string | number } | null
}

export type AssignmentResponse = {
  tracking_url: string
  qr_base64_png: string
}

export type ShipmentAssignment = {
  id: string
  shipment_id: string
  driver_id?: string | null
  tracking_code: string
  status: 'pending' | 'accepted' | 'rejected' | string
  created_at: string
}

// Compact assignment view embedded in shipment summaries. driver_name / driver_phone identify the
// actual driver who holds the assignment, resolved from their account — distinct from the planned
// driver a manager typed into route_json (open links are first-come, so whoever accepts can differ).
// Manager list/detail endpoints may both include it when the backend can resolve the latest
// assignment cheaply enough.
export type AssignmentSummary = {
  status: 'pending' | 'accepted' | 'rejected' | string
  driver_id?: string | null
  driver_name?: string
  driver_phone?: string
  // The accepting driver's own vehicle (type code + plate), pulled from their profile — the real
  // transport on the trip, distinct from the type the manager required in route_json.
  vehicle_type?: string
  vehicle_plate?: string
  // The driver's semi-trailer from their profile (20 т, сцепка, спецтранспорт).
  trailer_plate?: string
  tracking_code: string
  created_at: string
}

// ShipmentSummary is the enriched single-shipment detail (GET /api/shipments/:id): the core
// Shipment plus the resolved latest assignment and computed effective status.
export type ShipmentSummary = Shipment & {
  effective_status?: string
  assignment?: AssignmentSummary | null
  // A draft no driver has touched — it may be deleted, not only cancelled. Detail view only.
  can_delete?: boolean
}

export type DriverUser = {
  id: string
  role: string
  login: string
  email?: string | null
  first_name?: string | null
  last_name?: string | null
  company_id?: string | null
  created_at: string
}

// One line of «История событий». Title and description are the server's wording, so the site, the
// mobile site and the app say the same thing.
export type ShipmentHistoryEvent = {
  kind: string
  at: string
  title: string
  description?: string
}

// One line of the manager's bell (GET /api/manager/notifications): what a driver did with one of
// the manager's shipments, from the same event log and in the same wording as «История событий».
// `id` only grows, so a line newer than the last one seen is unread.
export type ManagerNotification = {
  id: string
  shipment_id: string
  kind: string
  at: string
  title: string
  description?: string
  shipment_title: string
  shipment_route?: string
  /** The part of the shipment a tap opens (lib/notificationTarget.ts); missing — the card itself. */
  focus?: string
}

// One line of the driver's bell (GET /api/driver/notifications): the push's own words, kept on the
// server whether or not the push was sent.
export type DriverNotification = {
  id: string
  shipment_id: string
  kind: string
  at: string
  title: string
  description?: string
}

// «Проверить уведомления»: where the server's test push is going and when it comes.
export type PushTestResult = { web: number; devices: number; delay_seconds: number }

// The driver's work over one period (GET /api/driver/stats). distance_km is the planned route
// distance of the finished trips — the estimate the one-off tracking price uses, not the GPS track.
export type DriverStatsPeriod = {
  since?: string
  trips: number
  // Kilometres by the GPS track, counted by the days the vehicle moved (not by when trips finished).
  distance_km: number
  // The part of distance_km bridged over signal gaps: above zero, the figure is approximate.
  estimated_km: number
  // false: no GPS fix in the period at all — distance_km is «no data», not 0 km.
  has_gps: boolean
  // Finished trips that never sent a fix, and the planned-route estimate of those that have a route.
  trips_without_gps: number
  route_km: number
  // Of trips_without_gps, those without route coordinates either.
  trips_without_route: number
}

export type DriverStats = {
  week: DriverStatsPeriod
  month: DriverStatsPeriod
  year: DriverStatsPeriod
  all: DriverStatsPeriod
}

export type Document = {
  id: string
  shipment_id: string
  document_type: string
  // Opaque storage key. Also doubles as the availability flag: the backend blanks it once the
  // retention window has passed. Never show it to a user — display_name is the readable name.
  file_path: string
  // Readable name derived server-side from shipment id + upload time.
  display_name?: string
  uploaded_at: string
  expired?: boolean
}

export function translateStatus(status: string): string {
  const statusMap: Record<string, string> = {
    'created': 'Создана',
    // `pending` is a legacy raw shipment value. Current manager responses use
    // `awaiting_driver` for the separate assignment-derived state.
    'pending': 'Создана',
    'assigned': 'Назначена',
    'awaiting_driver': 'Ожидает водителя',
    'driver_rejected': 'Водитель отказался',
    // Accepted is not started (26.10.2026): the trip runs only after the driver's «Начать рейс».
    'accepted': 'Принята водителем',
    'in_progress': 'В пути',
    'completed': 'Завершена',
    'delivered': 'Завершена',
    'cancelled': 'Отменена',
    'rejected': 'Отклонена',
  }
  return statusMap[status] || status
}

// Russian label for a transport code. Backed by the backend catalog cache (see
// lib/vehicleTypes); falls back to the code until the catalog loads. Kept exported here for
// the many call sites that already import it from this module.
export function translateVehicleType(type?: string): string {
  return vehicleTypeLabel(type)
}

// One shipment's answer to «Начать рейс»; code and message are those of an error response.
export type TripStartResult = {
  shipment_id: string
  started: boolean
  code?: string
  message?: string
}

export type ShipmentStatus =
  | 'created'
  | 'pending'
  | 'assigned'
  | 'awaiting_driver'
  | 'driver_rejected'
  | 'accepted'
  | 'in_progress'
  | 'completed'
  | 'delivered'
  | 'cancelled'

export type ManagerNote = {
  id: string
  text: string
  completed: boolean
  created_at: string
}

// One active Keycloak session for the current user. Keycloak only exposes IP + timestamps —
// no parsed device/browser or geolocation, so the UI shows exactly what is real.
export type UserSession = {
  id: string
  ip_address: string
  started_at: string // RFC3339
  last_access: string // RFC3339
  current: boolean
}

// ---- Analytics (server-computed) ----

export type AnalyticsPeriod = '8w' | '6m' | '12m'

// Created counts by created_at; completed/cancelled by completed_at (the day the trip ended).
export type AnalyticsTrendBucket = { start: string; created: number; completed: number; cancelled: number }
// Shipments created in the period by their current effective status (delivered is folded into completed).
export type AnalyticsStatusBucket = { status: string; count: number }
export type AnalyticsRouteBucket = { route: string; count: number; completed: number; cancelled: number }
// Shipments with at least one control task of the category; a shipment with problems of several
// kinds is counted in each of them.
// (stalled — «активна дольше 72 часов» — is gone since 26.10.2026: age is no reason.)
export type AttentionCategory = 'completion' | 'assignment' | 'geolocation' | 'late' | 'stops' | 'documents'
export type AnalyticsAttentionBucket = { key: AttentionCategory | string; count: number }

// «Текущее состояние» — independent of any period. The same numbers on every dashboard.
export type ManagerCurrentState = {
  active_shipments: number
  in_progress: number
  with_geolocation: number
  awaiting_assignment: number
  // Accepted by a driver, trip not started («Приняты, рейс не начат»).
  accepted?: number
  // Shipments with at least one control task («Заявки требуют внимания»)…
  attention_shipments: number
  // …and the tasks themselves («Что нужно сделать»).
  attention_tasks: number
  documents_pending: number
}

export type ManagerPeriodMetrics = {
  created: number
  completed: number
  cancelled: number
  driver_rejected: number
  shipments_with_documents: number
  documents_uploaded: number
}

export type ManagerAnalytics = {
  period: AnalyticsPeriod
  period_start: string
  generated_at: string
  current: ManagerCurrentState
  period_metrics: ManagerPeriodMetrics
  trend: AnalyticsTrendBucket[]
  status_distribution: AnalyticsStatusBucket[]
  popular_routes: AnalyticsRouteBucket[]
  attention_summary: AnalyticsAttentionBucket[]
}

export type ManagerControlTaskCode =
  | 'completion_requested'
  | 'missing_documents'
  | 'missing_assignment'
  | 'missing_vehicle'
  | 'missing_driver'
  | 'driver_rejected'
  | 'no_geolocation'
  | 'stale_geolocation'
  | 'late_loading'
  | 'late_unloading'
  | 'long_stop'
  | 'long_stay_loading'
  | 'long_stay_unloading'
  | 'needs_review'

// One reason a shipment needs the manager, decided on the server (models.BuildManagerShipmentState).
export type ManagerControlTask = {
  shipment_id: string
  code: ManagerControlTaskCode
  category: AttentionCategory
  priority: 'high' | 'medium'
  rank: number
  // The effective status the task was raised for — a manual acknowledgement is stored against it.
  status_code: string
  can_be_manually_resolved: boolean
  // A problem counted in «Требуют внимания»; false for the ordinary preparation steps
  // (missing driver or vehicle). The server decides, clients only read it.
  attention: boolean
  // Listed in «Контроль перевозок»: every attention task, and a finished shipment's documents during
  // the driver's two hours too (not red yet). Absent in an answer from before 30.10.2026.
  control?: boolean
  title: string
  route: string
  shipment_created_at: string
  trip_started_at?: string
  last_fix_at?: string
  completion_due_at?: string
  // late_loading / late_unloading: the planned time the truck is late for.
  planned_at?: string
  // long_stop / long_stay_*: since when the truck stands (the stop's first fix, or the arrival).
  standing_since?: string
}

// The manager's view of one shipment, embedded in every /api/shipments row.
export type ManagerShipmentState = {
  shipment_id: string
  effective_status: string
  active: boolean
  in_transit: boolean
  // Accepted by the driver, «Начать рейс» not pressed: neither waiting for a driver nor in transit.
  accepted?: boolean
  has_geolocation: boolean
  awaiting_assignment: boolean
  documents_pending: boolean
  control_tasks: ManagerControlTask[]
}

export type ManagerOperationalSummary = {
  generated_at: string
  current: ManagerCurrentState
  attention_summary: AnalyticsAttentionBucket[]
  tasks: ManagerControlTask[]
}

// A public tracking link the user saved to their cabinet ("полученные по ссылке"). Read-only
// bookmark over the public share token the user already holds — no operator rights implied.
export type SavedTrackingLink = {
  id: string
  user_id: string
  shipment_id: string
  token: string
  label: string
  /** Added client-side from the existing public tracking response; never stored by the API. */
  route?: string
  last_status: string
  created_at: string
}

/** A trip that is not finished yet, and therefore stands between the user and deleting. */
export type BlockingShipment = { id: string; status: string }

export type AccountDeletionStatus = {
  can_delete: boolean
  blocking: { as_owner: BlockingShipment[]; as_driver: BlockingShipment[] }
}

export type ConsentStatus = {
  /** true → we hold no personal-data consent that still counts; the consent screen must be shown. */
  required: boolean
  /**
   * Which kind of `required` this is: false — never asked, true — accepted an earlier revision
   * whose substance has since changed. Only the wording differs; both are equally blocking.
   */
  reconsent: boolean
  /** The revision currently on record, so the screen can name what is being superseded. */
  accepted_consent_version?: string
  consent_version: string
  terms_version: string
  /**
   * YYYY-MM-DD of the last acceptance of the user agreement (sign-up or the consent screen).
   * LegalUpdateNotice announces revisions dated after it on a browser that has none recorded.
   */
  terms_accepted_on?: string
}

export const api = {
  /**
   * 152-ФЗ consent gate. Two kinds of caller hit it: accounts created inside Keycloak (social
   * login via Яндекс / VK ID) that never passed our registration form, and accounts whose
   * recorded consent no longer covers the current document because its substance changed.
   */
  consentStatus(): Promise<ConsentStatus> {
    return request('/api/consent/status')
  },
  acceptConsent(): Promise<{ ok: boolean }> {
    return request('/api/consent', {
      method: 'POST',
      body: JSON.stringify({ pdn_consent_accepted: true, terms_accepted: true }),
    })
  },

  /**
   * Account deletion (152-ФЗ ст. 14 / ст. 9 ч. 2, п. 8.2 соглашения). Three steps on purpose:
   * the status call lets the UI warn about unfinished trips before the user commits, the code is
   * mailed to the registered address, and only the third call erases anything.
   */
  accountDeletionStatus(): Promise<AccountDeletionStatus> {
    return request('/api/profile/deletion')
  },
  requestAccountDeletionCode(): Promise<{ ok: boolean }> {
    return request('/api/profile/deletion/code', { method: 'POST' })
  },
  deleteAccount(code: string): Promise<{ ok: boolean }> {
    return request('/api/profile', { method: 'DELETE', body: JSON.stringify({ code }) })
  },

  async osrmRoute(points: [number, number][]): Promise<[number, number][]> {
    if (!points || points.length < 2) return []
    try {
      const coords = points.map(([lat, lng]) => `${lng},${lat}`).join(';')
      const path = `/api/osrm/route/v1/driving/${coords}?overview=full&geometries=geojson&steps=false&alternatives=false`
      try { await keycloak.updateToken(30) } catch { /* ignore */ }
      const res = await fetch(BASE_URL + path, {
        headers: keycloak.token ? { Authorization: `Bearer ${keycloak.token}` } : {},
        credentials: 'include',
      })
      if (!res.ok) return []
      const data = await res.json()
      const routeCoordinates = data?.routes?.[0]?.geometry?.coordinates as [number, number][] | undefined
      if (!routeCoordinates) return []
      return routeCoordinates.map(([lng, lat]) => [lat, lng])
    } catch {
      return []
    }
  },

  listShipments(): Promise<Shipment[]> {
    return request('/api/shipments')
  },
  // Server-filtered, paginated manager archive. Empty params return the first page of
  // completed shipments (the backend defaults status to "completed").
  listShipmentHistory(params: ShipmentHistoryParams = {}): Promise<ShipmentHistoryResult> {
    const qs = new URLSearchParams()
    if (params.status) qs.set('status', params.status)
    if (params.vehicleType) qs.set('vehicle_type', params.vehicleType)
    if (params.q) qs.set('q', params.q)
    if (params.from) qs.set('from', params.from)
    if (params.to) qs.set('to', params.to)
    if (params.limit != null) qs.set('limit', String(params.limit))
    if (params.offset != null) qs.set('offset', String(params.offset))
    const query = qs.toString()
    return request(`/api/shipments/history${query ? `?${query}` : ''}`)
  },
  fetchVehicleTypes(): Promise<{ vehicle_types: VehicleTypeOption[] }> {
    return publicRequest('/api/catalog/vehicle-types')
  },
  listDriverShipments(): Promise<Shipment[]> {
    return request('/api/driver/shipments')
  },
  listDriverPendingAssignments(): Promise<any[]> {
    return request('/api/driver/assignments/pending')
  },
  listDriverHistory(): Promise<any[]> {
    return request('/api/driver/history')
  },
  getDriverStats(): Promise<DriverStats> {
    return request('/api/driver/stats')
  },
  getProfile(): Promise<{
    id: string
    role: string
    login: string
    email: string | null
    first_name: string | null
    last_name: string | null
    company_id: string | null
    company: string
    created_at: string
    company_name?: string
    contact_name?: string
    phone?: string
    address?: string
    transport_type?: string
    vehicle_plate?: string
    trailer_plate?: string
  }> {
    return request('/api/profile')
  },
  updateProfile(data: {
    first_name?: string
    last_name?: string
    contact_name?: string
    phone?: string
    company_name?: string
    address?: string
    transport_type?: string
    vehicle_plate?: string
    trailer_plate?: string
  }): Promise<{ ok: boolean }> {
    return request('/api/profile', { method: 'PUT', body: JSON.stringify(data) })
  },
  getNotificationSettings(): Promise<NotificationSettingsDTO> {
    return request('/api/profile/notifications')
  },
  updateNotificationSettings(data: NotificationSettingsDTO): Promise<NotificationSettingsDTO> {
    return request('/api/profile/notifications', { method: 'PUT', body: JSON.stringify(data) })
  },
  getPushVapidKey(): Promise<{ enabled: boolean; public_key: string }> {
    return request('/api/push/vapid-key')
  },
  subscribePush(data: { endpoint: string; keys: { p256dh: string; auth: string } }): Promise<{ ok: boolean }> {
    return request('/api/push/subscribe', { method: 'POST', body: JSON.stringify(data) })
  },
  unsubscribePush(data: { endpoint: string }): Promise<{ ok: boolean }> {
    return request('/api/push/unsubscribe', { method: 'POST', body: JSON.stringify(data) })
  },
  /** A test push to the caller's own browsers and app installations, after delay_seconds. */
  sendTestPush(): Promise<PushTestResult> {
    return request('/api/push/test', { method: 'POST' })
  },
  /**
   * Drops this browser's push endpoint at sign-out. A bare fetch on purpose: `request` answers a
   * 401 with a fresh login, which would hijack the sign-out and leave the Keycloak session open.
   * Never throws — the local unsubscribe that follows stops delivery on its own.
   */
  async forgetPushEndpoint(endpoint: string): Promise<void> {
    const token = keycloak.token
    if (!token) return
    await fetch(`${BASE_URL}/api/push/unsubscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ endpoint }),
    }).catch(() => {})
  },
  createShipment(payload: CreateShipmentPayload): Promise<Shipment> {
    return request('/api/shipments', { method: 'POST', body: JSON.stringify(payload) })
  },
  updateShipment(id: string, payload: CreateShipmentPayload): Promise<Shipment> {
    return request(`/api/shipments/${id}`, { method: 'PUT', body: JSON.stringify(payload) })
  },
  async updateDriverComment(id: string, comment: string): Promise<Shipment> {
    const current = await this.getShipmentSummary(id)
    if (current.route_json == null || ['completed', 'delivered', 'cancelled'].includes(current.status)) {
      throw new Error('Заявка закрыта или её маршрут недоступен. Обновите данные и попробуйте ещё раз.')
    }
    return this.updateShipment(id, { route_json: current.route_json, planned_delivery_time: current.planned_delivery_time, driver_comment: comment })
  },
  completeShipment(id: string): Promise<Shipment> {
    return request(`/api/shipments/${id}/complete`, { method: 'POST' })
  },
  // Turns the driver's request to finish down: the trip goes on. Confirming is completeShipment.
  rejectCompletionRequest(id: string): Promise<Shipment> {
    return request(`/api/shipments/${id}/completion-request/reject`, { method: 'POST' })
  },
  // «Больше не требует контроля»: stores only the mark for one control reason, never a full edit
  // (a full edit re-validates the whole route and fails on shipments with older route data).
  acknowledgeControl(id: string, code: string, statusCode: string): Promise<Shipment> {
    return request(`/api/shipments/${id}/control-acknowledgements/${encodeURIComponent(code)}`, {
      method: 'PUT',
      body: JSON.stringify({ status_code: statusCode }),
    })
  },
  // Calls the trip off but keeps it: the driver sees the cancellation and stops tracking.
  cancelShipment(id: string): Promise<Shipment> {
    return request(`/api/shipments/${id}/cancel`, { method: 'POST' })
  },
  deleteShipment(id: string): Promise<void> {
    return request(`/api/shipments/${id}`, { method: 'DELETE' })
  },
  assignShipment(id: string): Promise<AssignmentResponse> {
    const base = window.location.origin
    return request(`/api/shipments/${id}/assign`, {
      method: 'POST',
      headers: {
        'X-External-BaseURL': base,
        'Content-Type': 'application/json',
      },
    })
  },
  getAssignment(shipmentId: string): Promise<ShipmentAssignment> {
    return request(`/api/shipments/${shipmentId}/assignment`)
  },
  // Per-shipment detail with the resolved accepting driver (name/phone) embedded in `assignment`.
  getShipmentSummary(id: string): Promise<ShipmentSummary> {
    return request(`/api/shipments/${id}`)
  },
  assignShipmentToDriver(shipmentId: string, driverId: string): Promise<AssignmentResponse> {
    const base = window.location.origin
    return request(`/api/shipments/${shipmentId}/assign-to-driver`, {
      method: 'POST',
      headers: {
        'X-External-BaseURL': base,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ driver_id: driverId }),
    })
  },
  listDrivers(): Promise<DriverUser[]> {
    return request('/api/drivers')
  },
  /** Product owners only (backend config analytics.ownerEmails); 404 for everyone else. */
  getDriverFunnel(days: number): Promise<DriverFunnelReport> {
    return request(`/api/internal/driver-funnel?days=${days}`)
  },
  /** Product owners only, like the driver funnel. */
  getGrowthReport(days: number): Promise<GrowthReport> {
    return request(`/api/internal/growth?days=${days}`)
  },
  /** Marketing touches the site kept before sign-up (lib/attribution.ts). */
  saveAttribution(first: unknown, last: unknown): Promise<void> {
    return request('/api/profile/attribution', { method: 'POST', body: JSON.stringify({ first, last }) })
  },
  /** Whether the manager already answered «Откуда узнали о Лупе?» (or chose not to). */
  getSelfReportedSource(): Promise<{ answered: boolean }> {
    return request('/api/profile/source')
  },
  saveSelfReportedSource(answer: string, other = ''): Promise<void> {
    return request('/api/profile/source', { method: 'PUT', body: JSON.stringify({ answer, other }) })
  },
  /** A UI action for the growth report (lib/productEvents.ts). */
  recordProductEvent(event: string, context = ''): Promise<void> {
    return request('/api/events', { method: 'POST', body: JSON.stringify({ event, context }) })
  },
  driverGetByCode(code: string): Promise<any> {
    return request(`/api/track/${code}`)
  },
  driverGetByEncryptedData(encryptedData: string): Promise<any> {
    return request(`/api/track?data=${encodeURIComponent(encryptedData)}`)
  },
  driverAccept(code: string, locationConsent = false): Promise<any> {
    return request(`/api/track/${code}/accept`, {
      method: 'POST',
      body: JSON.stringify({ location_consent: locationConsent }),
    })
  },
  driverReject(code: string): Promise<any> {
    return request(`/api/track/${code}/reject`, { method: 'POST' })
  },
  // «Начать рейс» for one or several accepted shipments (consolidated cargo): each starts on its own,
  // the answer says which did and why the others did not (its operator's tariff, a cancellation…).
  // locationConsent: the driver allows their position on the customers' public links (152-ФЗ).
  driverStartTrips(shipmentIds: string[], locationConsent: boolean): Promise<{ results: TripStartResult[] }> {
    return request('/api/driver/trips/start', {
      method: 'POST',
      body: JSON.stringify({ shipment_ids: shipmentIds, location_consent: locationConsent }),
    })
  },
  // Turns down a trip the driver accepted but has not started; it goes back to the manager.
  driverDeclineAccepted(shipmentId: string): Promise<{ rejected: boolean }> {
    return request(`/api/driver/shipments/${shipmentId}/decline`, { method: 'POST' })
  },
  sendLocation(data: { latitude: number; longitude: number; accuracy?: number; shipment_id: string }): Promise<void> {
    return request('/api/track', { method: 'POST', body: JSON.stringify(data) })
  },
  /** Batch GPS upload — drains the offline buffer; `ts` is capture time in epoch ms. */
  sendLocations(data: {
    shipment_id: string
    points: { latitude: number; longitude: number; accuracy?: number; ts: number }[]
  }): Promise<void> {
    return request('/api/track/batch', { method: 'POST', body: JSON.stringify(data) })
  },
  async uploadDocument(id: string, file: File, documentType: UploadDocumentType = 'image'): Promise<Document> {
    const fd = new FormData()
    fd.append('document_type', documentType)
    fd.append('file', await prepareUploadImage(file))
    return request(`/api/shipments/${id}/documents`, { method: 'POST', body: fd })
  },
  // Asks the manager to finish the trip. The trip keeps running (and tracking) until the manager
  // answers or completion_due_at passes.
  driverRequestComplete(id: string): Promise<{ ok: boolean; shipment?: Shipment }> {
    return request(`/api/driver/shipments/${id}/complete`, { method: 'POST' })
  },
  // Takes the driver's own open request back.
  driverWithdrawComplete(id: string): Promise<{ ok: boolean; shipment?: Shipment }> {
    return request(`/api/driver/shipments/${id}/complete`, { method: 'DELETE' })
  },
  publicTrack(shipmentId: string, token: string): Promise<{
    shipment_id: string
    status: string
    positions: [number, number][]
    last?: { latitude: number; longitude: number; timestamp: string }
    tracking_active?: boolean
    route_points?: [number, number][]
    planned_polyline?: [number, number][]
    route?: string
  }> {
    // Public share link: token-only, no Keycloak — a logged-out customer must not be bounced to login.
    return publicRequest(`/api/public/track/${shipmentId}?token=${encodeURIComponent(token)}`)
  },
  listDocuments(id: string): Promise<Document[]> {
    return request(`/api/shipments/${id}/documents`)
  },
  listShipmentEvents(id: string): Promise<ShipmentHistoryEvent[]> {
    return request(`/api/shipments/${encodeURIComponent(id)}/events`)
  },
  // The bell: the newest lines across all of the manager's shipments.
  listManagerNotifications(): Promise<ManagerNotification[]> {
    return request('/api/manager/notifications')
  },
  // The driver's bell: what the manager did with the driver's shipments, and the reminders.
  listDriverNotifications(): Promise<DriverNotification[]> {
    return request('/api/driver/notifications')
  },
  // «Понятно» on a shipment the manager cancelled: it leaves the block and stays in the history.
  markDriverCancelSeen(shipmentId: string): Promise<{ ok: boolean }> {
    return request(`/api/driver/shipments/${encodeURIComponent(shipmentId)}/cancel-seen`, { method: 'POST' })
  },
  // The driver's view of the same log: only a driver who accepted the trip, and only from the
  // moment they accepted it (the server cuts it).
  listDriverShipmentEvents(id: string): Promise<ShipmentHistoryEvent[]> {
    return request(`/api/driver/shipments/${encodeURIComponent(id)}/events`)
  },
  // "Полученные по ссылке": the caller's saved public tracking links. Any authenticated user
  // (manager or driver) may bookmark a link they received; these never touch the shipment owner.
  listSavedLinks(): Promise<SavedTrackingLink[]> {
    return request('/api/tracking/saved')
  },
  saveTrackingLink(input: { shipment_id: string; token: string; label?: string; last_status?: string }): Promise<SavedTrackingLink> {
    return request('/api/tracking/saved', { method: 'POST', body: JSON.stringify(input) })
  },
  deleteSavedLink(shipmentId: string): Promise<{ ok: boolean }> {
    return request(`/api/tracking/saved/${encodeURIComponent(shipmentId)}`, { method: 'DELETE' })
  },
  listManagerDocuments(): Promise<Document[]> {
    return request('/api/documents')
  },
  // Server-computed manager analytics (trend/status/routes/attention) — no client aggregation.
  getManagerAnalytics(period: AnalyticsPeriod = '8w'): Promise<ManagerAnalytics> {
    return request(`/api/analytics/summary?period=${period}`)
  },
  // The dashboards' counters and whole control queue — the same states analytics' current block uses.
  getOperationalSummary(): Promise<ManagerOperationalSummary> {
    return request('/api/manager/operational-summary')
  },
  listDriverDocuments(id: string): Promise<Document[]> {
    return request(`/api/driver/shipments/${id}/documents`)
  },
  // The manager's files for the driver: the owner manages them, the accepted driver reads them.
  listAttachments(shipmentId: string): Promise<ShipmentAttachment[]> {
    return request(`/api/shipments/${encodeURIComponent(shipmentId)}/attachments`)
  },
  listDriverAttachments(shipmentId: string): Promise<ShipmentAttachment[]> {
    return request(`/api/driver/shipments/${encodeURIComponent(shipmentId)}/attachments`)
  },
  async uploadAttachment(shipmentId: string, file: File): Promise<ShipmentAttachment> {
    const fd = new FormData()
    // Photos are shrunk (and HEIC turned into JPEG) on the device, like the driver's documents.
    fd.append('file', await prepareUploadImage(file))
    return request(`/api/shipments/${encodeURIComponent(shipmentId)}/attachments`, { method: 'POST', body: fd })
  },
  deleteAttachment(shipmentId: string, attachmentId: string): Promise<{ deleted: boolean }> {
    return request(`/api/shipments/${encodeURIComponent(shipmentId)}/attachments/${encodeURIComponent(attachmentId)}`, { method: 'DELETE' })
  },
  // Manager-scoped snapshot for a shipment they own — full driven GPS track + last fix.
  getTrackingSnapshot(id: string): Promise<TrackSnapshot> {
    return request(`/api/shipments/${id}/tracking-snapshot`)
  },
  // Manager batch: snapshots for many owned shipments in one request. The server caps a
  // batch at 50 ids and silently skips shipments the manager doesn't own.
  getTrackingSnapshots(ids: string[]): Promise<TrackSnapshot[]> {
    return request('/api/shipments/tracking-snapshots', { method: 'POST', body: JSON.stringify({ ids }) })
  },
  generatePublicToken(id: string): Promise<{ token: string }> {
    return request(`/api/shipments/${id}/public-token`, { method: 'POST' })
  },
  // Revoke every outstanding public tracking link for the shipment and get a fresh one to share.
  revokePublicToken(id: string): Promise<{ token: string }> {
    return request(`/api/shipments/${id}/public-token/revoke`, { method: 'POST' })
  },
  getPlan(): Promise<PlanStatus> {
    return request('/api/billing/plan')
  },
  // payments_available is false when no payment gateway is configured (production before YooKassa
  // is connected): the plans are listed, but nothing can be bought — an older backend omits it.
  getBillingCatalog(): Promise<{ plans: PlanCatalogItem[]; payments_available?: boolean }> {
    return request('/api/billing/catalog')
  },
  getPlanOffers(): Promise<{ offers: PlanOffer[]; payments_available?: boolean }> {
    return request('/api/billing/plan/offers')
  },
  createPlanCheckout(planCode: string): Promise<CheckoutResult> {
    return request('/api/billing/plan/checkout', { method: 'POST', body: JSON.stringify({ plan_code: planCode }) })
  },
  // Top up the «отслеживания» wallet by `units` (each covers up to 3 суток). units defaults to 1.
  createOneOffCheckout(units = 1): Promise<CheckoutResult> {
    return request('/api/billing/oneoff/checkout', { method: 'POST', body: JSON.stringify({ units }) })
  },
  // Reconcile a payment on return from the payment page; status says where it stands now.
  syncPayment(order: string): Promise<{ ok: boolean; status?: PaymentSyncStatus; kind?: PaymentKind }> {
    return request('/api/billing/payments/sync', { method: 'POST', body: JSON.stringify({ order }) })
  },
  getPaymentHistory(offset = 0, limit = 20): Promise<{ items: PaymentHistoryItem[]; has_more: boolean }> {
    return request(`/api/billing/payments?limit=${limit}&offset=${offset}`)
  },
  getCreditEvents(offset = 0, limit = 20): Promise<{ items: CreditEvent[]; has_more: boolean }> {
    return request(`/api/billing/credits?limit=${limit}&offset=${offset}`)
  },
  getBillingUsage(): Promise<BillingUsage> {
    return request('/api/billing/usage')
  },
  // Server-authoritative quote (days/units/price) for funding a route from the wallet.
  quoteTracking(points: GeoPoint[]): Promise<TrackingQuote> {
    return request('/api/billing/tracking/quote', { method: 'POST', body: JSON.stringify({ points }) })
  },
  // Choose how a shipment's tracking is paid for. For wallet funding, pass the route waypoints so
  // the server computes the number of «отслеживания» (it never trusts a client-side estimate).
  setTrackingFunding(shipmentId: string, funding: 'subscription' | 'wallet', points: GeoPoint[] = []): Promise<Shipment> {
    return request(`/api/shipments/${shipmentId}/tracking/funding`, {
      method: 'POST',
      body: JSON.stringify({ funding, points }),
    })
  },
  // Extend a running wallet-funded trip's lease by `units` «отслеживания» (+3 суток each).
  renewTracking(shipmentId: string, units = 1): Promise<Shipment> {
    return request(`/api/shipments/${shipmentId}/tracking/renew`, { method: 'POST', body: JSON.stringify({ units }) })
  },
  getReferral(): Promise<ReferralStatus> {
    return request('/api/referral')
  },
  claimReferral(code: string): Promise<{ ok: boolean; already_referred?: boolean }> {
    return request('/api/referral/claim', { method: 'POST', body: JSON.stringify({ code }) })
  },

  listNotes(): Promise<ManagerNote[]> {
    return request('/api/notes')
  },
  createNote(text: string): Promise<ManagerNote> {
    return request('/api/notes', { method: 'POST', body: JSON.stringify({ text }) })
  },
  updateNote(id: string, completed: boolean): Promise<{ ok: boolean }> {
    return request(`/api/notes/${id}`, { method: 'PATCH', body: JSON.stringify({ completed }) })
  },
  deleteNote(id: string): Promise<{ ok: boolean }> {
    return request(`/api/notes/${id}`, { method: 'DELETE' })
  },

  changePassword(): Promise<{ ok: boolean; message: string }> {
    return request('/api/profile/change-password', { method: 'POST' })
  },

  changeEmail(): Promise<{ ok: boolean; message: string }> {
    return request('/api/profile/change-email', { method: 'POST' })
  },

  // Self-serve grant of an application role (e.g. a driver opening the manager cabinet).
  // The new role only takes effect after the client force-refreshes its token.
  grantRole(role: 'manager' | 'driver'): Promise<{ ok: boolean }> {
    return request('/api/profile/roles', { method: 'POST', body: JSON.stringify({ role }) })
  },

  // Active Keycloak sessions for the current user (read-only) + revoke one by id.
  listSessions(): Promise<{ sessions: UserSession[] }> {
    return request('/api/profile/sessions')
  },
  revokeSession(id: string): Promise<{ ok: boolean }> {
    return request(`/api/profile/sessions/${id}`, { method: 'DELETE' })
  },

  // Route planning: points split between vehicles. Available only where the backend has
  // routePlanning.enabled, the page itself only behind VITE_ROUTE_PLANNING.
  planRoutes(payload: RoutePlanRequest): Promise<RoutePlan> {
    return request('/api/route-plans', { method: 'POST', body: JSON.stringify(payload) })
  },
  suggestAddress(q: string, limit = 3): Promise<Array<{ address: string; city?: string; lat?: number; lon?: number }>> {
    return request(`/api/address/suggest?q=${encodeURIComponent(q)}&limit=${limit}`)
  },
  async orsRoute(points: [number, number][], opts?: { skipRussiaConstraints?: boolean }): Promise<[number, number][]> {
    if (!points || points.length < 2) return []
    try {
      const haversineKm = (a: [number, number], b: [number, number]) => {
        const toRad = (v: number) => (v * Math.PI) / 180
        const R = 6371
        const dLat = toRad(b[0] - a[0])
        const dLng = toRad(b[1] - a[1])
        const lat1 = toRad(a[0])
        const lat2 = toRad(b[0])
        const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
        return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x))
      }

      let approxKm = 0
      for (let i = 0; i < points.length - 1; i++) approxKm += haversineKm(points[i], points[i + 1])

      let waypoints: [number, number][] = [...points]
      if (!opts?.skipRussiaConstraints && approxKm > MAX_ORS_SEGMENT_KM && points.length === 2) {
        const [start, end] = points
        const minLng = Math.min(start[1], end[1])
        const maxLng = Math.max(start[1], end[1])
        const anchors = TRANS_SIBERIAN_CORRIDOR
          .filter(([, lng]) => lng > minLng && lng < maxLng)
          .sort((a, b) => a[1] - b[1])
        waypoints = [start, ...anchors, end]
      }

      const segments: [number, number][][] = []
      let current: [number, number][] = [waypoints[0]]
      for (let i = 1; i < waypoints.length; i++) {
        const prev = current[current.length - 1]
        const next = waypoints[i]
        const segKm = haversineKm(prev, next)
        if (segKm > MAX_ORS_SEGMENT_KM) {
          const mid: [number, number] = [(prev[0] + next[0]) / 2, (prev[1] + next[1]) / 2]
          waypoints.splice(i, 0, mid)
          i--
          continue
        }
        current.push(next)
        if (i < waypoints.length - 1) {
          segments.push(current)
          current = [next]
        }
      }
      segments.push(current)

      const allCoords: [number, number][] = []
      for (const seg of segments) {
        const coordinates = seg.map(([lat, lng]) => [lng, lat])
        const body = opts?.skipRussiaConstraints
          ? { coordinates } as any
          : {
              coordinates,
              options: {
                avoid_borders: 'all',
                avoid_countries: ORS_AVOID_COUNTRIES,
              },
            } as any

        try {
          await keycloak.updateToken(30)
        } catch { /* ignore */ }

        const res = await fetch(BASE_URL + '/api/ors/v2/directions/driving-car/geojson', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(keycloak.token ? { Authorization: `Bearer ${keycloak.token}` } : {}),
          },
          credentials: 'include',
          body: JSON.stringify(body),
        })
        if (!res.ok) {
          console.warn('[ORS] Segment non-OK response', res.status)
          return []
        }
        const data = await res.json()
        const coords = data?.features?.[0]?.geometry?.coordinates as [number, number][] | undefined
        if (!coords) return []
        const converted = coords.map(([lng, lat]) => [lat, lng]) as [number, number][]
        if (allCoords.length > 0) converted.shift()
        allCoords.push(...converted)
      }
      return allCoords
    } catch (e) {
      console.warn('[ORS] Request failed, falling back to OSRM', e)
      return []
    }
  },
}
